import React, { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * 2026-02 — Business email slide-over panel.
 *
 * Opened from the "View Email" inline action on a
 * `business_email_received` notification. Shows the full email + any
 * thread history + Reply / Mark as Lead / Close actions.
 */
export default function BusinessEmailSlideOver({ alert, onClose, onMarkedAsLead, onReplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [marking, setMarking] = useState(false);
  const [flashMsg, setFlashMsg] = useState('');

  useEffect(() => {
    if (!alert) return;
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/business-email/${alert._id}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load');
        const d = await res.json();
        if (!alive) return;
        setData(d);
        setReplySubject((d.subject || '').startsWith('Re:') ? d.subject : `Re: ${d.subject || ''}`);
      } catch (e) {
        if (alive) setError(e.message || 'Could not load email');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [alert]);

  if (!alert) return null;

  const submitReply = async () => {
    if (!replyBody.trim()) { setError('Reply body is required'); return; }
    setSending(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/business-email/${alert._id}/reply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: replySubject, body_text: replyBody }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Send failed'); }
      setComposerOpen(false);
      setReplyBody('');
      setFlashMsg('Reply sent.');
      onReplied && onReplied();
      setTimeout(() => setFlashMsg(''), 2500);
    } catch (e) {
      setError(e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const markAsLead = async () => {
    setMarking(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/business-email/${alert._id}/mark-as-lead`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Mark failed'); }
      const out = await res.json();
      onMarkedAsLead && onMarkedAsLead(alert._id, out.message);
    } catch (e) {
      setError(e.message || 'Could not reclassify');
    } finally {
      setMarking(false);
    }
  };

  return (
    <div data-testid="bizmail-slideover-overlay"
         onClick={onClose}
         style={{ position: 'fixed', inset: 0,
                  background: 'rgba(15,23,42,0.45)',
                  display: 'flex', justifyContent: 'flex-end', zIndex: 9700 }}>
      <div onClick={(e) => e.stopPropagation()}
           data-testid="bizmail-slideover"
           style={{ width: 'min(640px, 96vw)', height: '100%',
                    background: '#fff', boxShadow: '-12px 0 30px rgba(15,23,42,0.25)',
                    overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', padding: '1rem 1.25rem',
                      borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#4338ca', fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Business email
            </div>
            <h3 style={{ margin: '4px 0 0', color: '#0f172a', fontSize: '1.05rem' }}>
              {data?.subject || alert.title || '(no subject)'}
            </h3>
            <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 4 }}>
              From: <strong>{data?.sender_name || ''}</strong> &lt;{data?.sender_email || alert.reference_id}&gt;
            </div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="bizmail-close"
                  style={{ background: 'transparent', border: 'none',
                           fontSize: '1.4rem', cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        <div style={{ padding: '1rem 1.25rem', flex: 1 }}>
          {loading && <div style={{ color: '#64748b' }}>Loading…</div>}
          {error && (
            <div data-testid="bizmail-error"
                 style={{ color: '#b91c1c', background: '#fef2f2',
                          border: '1px solid #fecaca', padding: '0.6rem 0.8rem',
                          borderRadius: 8, marginBottom: 10 }}>
              {error}
            </div>
          )}
          {flashMsg && (
            <div data-testid="bizmail-flash"
                 style={{ background: '#ecfdf5', color: '#065f46',
                          border: '1px solid #a7f3d0', padding: '0.55rem 0.8rem',
                          borderRadius: 8, marginBottom: 10, fontSize: '0.9rem' }}>
              {flashMsg}
            </div>
          )}

          {data && (
            <>
              <div style={{ whiteSpace: 'pre-wrap', color: '#1e293b',
                            background: '#fafafa', padding: '0.85rem 1rem',
                            border: '1px solid #e2e8f0', borderRadius: 8,
                            fontSize: '0.92rem', lineHeight: 1.55 }}
                   data-testid="bizmail-body">
                {data.body_text || '(no body)'}
              </div>
              {data.thread && data.thread.length > 1 && (
                <details style={{ marginTop: 14 }}>
                  <summary style={{ cursor: 'pointer', color: '#475569',
                                    fontSize: '0.85rem', fontWeight: 600 }}>
                    {data.thread.length - 1} earlier message(s) in this thread
                  </summary>
                  <div style={{ marginTop: 8 }}>
                    {data.thread.slice(1).map(t => (
                      <div key={t.id}
                           style={{ borderLeft: '3px solid #cbd5e1',
                                    paddingLeft: 10, marginBottom: 10 }}>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {t.direction === 'outbound' ? '→ Sent' : '← Received'} · {t.created_at?.slice(0,16).replace('T',' ')}
                        </div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
                          {t.subject}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#475569',
                                      whiteSpace: 'pre-wrap', marginTop: 4 }}>
                          {(t.body_text || '').slice(0, 300)}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {composerOpen && (
                <div data-testid="bizmail-composer"
                     style={{ marginTop: 14, padding: '0.85rem 1rem',
                              background: '#fff', border: '1px solid #cbd5e1',
                              borderRadius: 8 }}>
                  <label style={{ fontSize: '0.72rem', color: '#334155',
                                  fontWeight: 600, textTransform: 'uppercase',
                                  letterSpacing: '0.05em' }}>Subject</label>
                  <input type="text" value={replySubject}
                          onChange={e => setReplySubject(e.target.value)}
                          data-testid="bizmail-reply-subject"
                          style={{ width: '100%', padding: '0.5rem 0.65rem',
                                   marginTop: 4, border: '1px solid #cbd5e1',
                                   borderRadius: 6, fontSize: '0.9rem' }} />
                  <label style={{ fontSize: '0.72rem', color: '#334155',
                                  fontWeight: 600, textTransform: 'uppercase',
                                  letterSpacing: '0.05em', marginTop: 10,
                                  display: 'block' }}>Reply</label>
                  <textarea value={replyBody}
                            onChange={e => setReplyBody(e.target.value)}
                            rows={8} data-testid="bizmail-reply-body"
                            placeholder="Write your reply…"
                            style={{ width: '100%', padding: '0.6rem 0.7rem',
                                     marginTop: 4, border: '1px solid #cbd5e1',
                                     borderRadius: 6, fontSize: '0.9rem',
                                     lineHeight: 1.5, fontFamily: 'inherit' }} />
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '0.9rem 1.25rem',
                      borderTop: '1px solid #e2e8f0', background: '#f8fafc',
                      justifyContent: 'flex-end' }}>
          {composerOpen ? (
            <>
              <button type="button" onClick={() => setComposerOpen(false)}
                      disabled={sending}
                      data-testid="bizmail-cancel-reply"
                      style={{ background: '#e2e8f0', color: '#0f172a',
                               border: 'none', borderRadius: 6,
                               padding: '0.5rem 1rem', fontWeight: 600,
                               cursor: sending ? 'not-allowed' : 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={submitReply} disabled={sending}
                      data-testid="bizmail-send-reply"
                      style={{ background: sending ? '#94a3b8' : '#0073EA',
                               color: '#fff', border: 'none', borderRadius: 6,
                               padding: '0.5rem 1.1rem', fontWeight: 600,
                               cursor: sending ? 'not-allowed' : 'pointer' }}>
                {sending ? 'Sending…' : 'Send reply'}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={markAsLead} disabled={marking}
                      data-testid="bizmail-mark-as-lead"
                      style={{ background: marking ? '#94a3b8' : '#f59e0b',
                               color: '#fff', border: 'none', borderRadius: 6,
                               padding: '0.5rem 1rem', fontWeight: 600,
                               cursor: marking ? 'not-allowed' : 'pointer' }}>
                {marking ? 'Reclassifying…' : 'Mark as Lead'}
              </button>
              <button type="button" onClick={() => setComposerOpen(true)}
                      data-testid="bizmail-open-reply"
                      style={{ background: '#0073EA', color: '#fff',
                               border: 'none', borderRadius: 6,
                               padding: '0.5rem 1.1rem', fontWeight: 600,
                               cursor: 'pointer' }}>
                Reply
              </button>
              <button type="button" onClick={onClose}
                      data-testid="bizmail-close-bottom"
                      style={{ background: '#e2e8f0', color: '#0f172a',
                               border: 'none', borderRadius: 6,
                               padding: '0.5rem 1rem', fontWeight: 600,
                               cursor: 'pointer' }}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
